import React, { useState } from 'react';
import styles from './WriteFeedback.module.css';

const WriteFeedback = () => {
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    email: '',
    phone: '',
    studentId: '',
    
    // Course Information
    courseEnrolled: '',
    batchName: '',
    yearOfEnrollment: '',
    
    // Feedback Categories
    feedbackType: 'general',
    rating: 0,
    
    // Detailed Feedback
    subject: '',
    feedbackMessage: '',
    suggestions: '',
    
    // Specific Areas
    facultyRating: 0,
    contentQuality: 0,
    doubtResolution: 0,
    testSeries: 0,
    platformExperience: 0,
    
    // Would Recommend
    recommend: '',
    
    // Additional
    allowContact: false,
    anonymous: false
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });

  const [hoverRating, setHoverRating] = useState(0);
  const [hoverFaculty, setHoverFaculty] = useState(0);
  const [hoverContent, setHoverContent] = useState(0);
  const [hoverDoubt, setHoverDoubt] = useState(0);
  const [hoverTest, setHoverTest] = useState(0);
  const [hoverPlatform, setHoverPlatform] = useState(0);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRatingChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simulate form submission
    setFormStatus({
      submitted: true,
      success: true,
      message: 'Thank you for your valuable feedback! We appreciate your time and effort in helping us improve.'
    });
    
    // Reset form after submission
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      studentId: '',
      courseEnrolled: '',
      batchName: '',
      yearOfEnrollment: '',
      feedbackType: 'general',
      rating: 0,
      subject: '',
      feedbackMessage: '',
      suggestions: '',
      facultyRating: 0,
      contentQuality: 0,
      doubtResolution: 0,
      testSeries: 0,
      platformExperience: 0,
      recommend: '',
      allowContact: false,
      anonymous: false
    });

    setHoverRating(0);
    setHoverFaculty(0);
    setHoverContent(0);
    setHoverDoubt(0);
    setHoverTest(0);
    setHoverPlatform(0);

    // Clear success message after 5 seconds
    setTimeout(() => {
      setFormStatus({
        submitted: false,
        success: false,
        message: ''
      });
    }, 5000);
  };

  const renderStars = (name, currentRating, hover, setHover) => {
    return (
      <div className={styles.starRating}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`${styles.star} ${
              star <= (hover || currentRating) ? styles.starFilled : ''
            }`}
            onClick={() => handleRatingChange(name, star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const feedbackTypes = [
    { value: 'general', label: 'General Feedback' },
    { value: 'course', label: 'Course Related' },
    { value: 'faculty', label: 'Faculty Review' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'suggestion', label: 'Suggestion' },
    { value: 'complaint', label: 'Complaint' },
    { value: 'appreciation', label: 'Appreciation' },
    { value: 'other', label: 'Other' }
  ];

  const courses = [
    'UPSC Mathematics Optional 2026',
    'UPSC Mathematics Optional 2027',
    'BPSC Mathematics Optional',
    'CSIR NET/GATE Mathematics',
    'Mathematics Foundation',
    'Chapterwise Test Series',
    'Full Syllabus Test Series',
    'Mentorship Program'
  ];

  const batches = [
    'Weekday Batch',
    'Weekend Batch',
    'Morning Batch',
    'Evening Batch',
    'Fast Track Batch',
    'Self Paced'
  ];

  const years = ['2023', '2024', '2025', '2026', '2027'];

  return (
    <div className={styles.feedbackPage}>
      {/* Header */}
      <div className={styles.feedbackHeader}>
        <div className={styles.container}>
          <h1>Write a <span className={styles.gradientText}>Feedback</span></h1>
          <p>Your insights help us create a better learning experience for thousands of aspirants.</p>
        </div>
        <div className={styles.headerWave}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120">
            <path fill="#EDFFF7" fillOpacity="1" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
          </svg>
        </div>
      </div>

      <div className={styles.container}>
        {/* Progress Bar */}
        <div className={styles.progressSection}>
          <div className={styles.progressSteps}>
            <div className={styles.progressStep}>
              <span className={styles.stepNumber}>1</span>
              <span className={styles.stepLabel}>Personal Info</span>
            </div>
            <div className={styles.progressLine}></div>
            <div className={styles.progressStep}>
              <span className={styles.stepNumber}>2</span>
              <span className={styles.stepLabel}>Course Details</span>
            </div>
            <div className={styles.progressLine}></div>
            <div className={styles.progressStep}>
              <span className={styles.stepNumber}>3</span>
              <span className={styles.stepLabel}>Ratings</span>
            </div>
            <div className={styles.progressLine}></div>
            <div className={styles.progressStep}>
              <span className={styles.stepNumber}>4</span>
              <span className={styles.stepLabel}>Feedback</span>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {formStatus.submitted && formStatus.success && (
          <div className={styles.successMessage}>
            <div className={styles.successIcon}>✓</div>
            <div className={styles.successContent}>
              <h3>Thank You!</h3>
              <p>{formStatus.message}</p>
            </div>
          </div>
        )}

        {/* Main Form */}
        <form onSubmit={handleSubmit} className={styles.feedbackForm}>
          {/* Section 1: Personal Information */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIcon}>👤</span>
              <h2>Personal Information</h2>
              <p className={styles.sectionSubtitle}>Help us identify you better (optional unless you choose anonymous)</p>
            </div>

            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="fullName">
                  Full Name <span className={styles.optional}>(Optional)</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className={styles.formInput}
                  disabled={formData.anonymous}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="email">
                  Email Address <span className={styles.optional}>(Optional)</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className={styles.formInput}
                  disabled={formData.anonymous}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="phone">
                  Phone Number <span className={styles.optional}>(Optional)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  className={styles.formInput}
                  disabled={formData.anonymous}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="studentId">
                  Student ID <span className={styles.optional}>(Optional)</span>
                </label>
                <input
                  type="text"
                  id="studentId"
                  name="studentId"
                  value={formData.studentId}
                  onChange={handleChange}
                  placeholder="e.g., EXA-2026-001"
                  className={styles.formInput}
                  disabled={formData.anonymous}
                />
              </div>
            </div>

            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="anonymous"
                  checked={formData.anonymous}
                  onChange={handleChange}
                  className={styles.checkbox}
                />
                <span className={styles.checkboxText}>
                  <strong>Submit Anonymously</strong> - Your personal information will not be shared
                </span>
              </label>
            </div>
          </div>

          {/* Section 2: Course Information */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIcon}>📚</span>
              <h2>Course Information</h2>
              <p className={styles.sectionSubtitle}>Tell us which course you're enrolled in</p>
            </div>

            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="courseEnrolled">
                  Course Enrolled <span className={styles.required}>*</span>
                </label>
                <select
                  id="courseEnrolled"
                  name="courseEnrolled"
                  value={formData.courseEnrolled}
                  onChange={handleChange}
                  required
                  className={styles.formSelect}
                >
                  <option value="">Select a course</option>
                  {courses.map(course => (
                    <option key={course} value={course}>{course}</option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="batchName">
                  Batch Name
                </label>
                <select
                  id="batchName"
                  name="batchName"
                  value={formData.batchName}
                  onChange={handleChange}
                  className={styles.formSelect}
                >
                  <option value="">Select batch (optional)</option>
                  {batches.map(batch => (
                    <option key={batch} value={batch}>{batch}</option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="yearOfEnrollment">
                  Year of Enrollment
                </label>
                <select
                  id="yearOfEnrollment"
                  name="yearOfEnrollment"
                  value={formData.yearOfEnrollment}
                  onChange={handleChange}
                  className={styles.formSelect}
                >
                  <option value="">Select year (optional)</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Section 3: Detailed Ratings */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIcon}>⭐</span>
              <h2>Rate Your Experience</h2>
              <p className={styles.sectionSubtitle}>Help us understand what we're doing well and where we can improve</p>
            </div>

            <div className={styles.ratingsGrid}>
              <div className={styles.ratingItem}>
                <label>Overall Experience</label>
                {renderStars('rating', formData.rating, hoverRating, setHoverRating)}
                <span className={styles.ratingLabel}>
                  {formData.rating === 1 && 'Poor'}
                  {formData.rating === 2 && 'Fair'}
                  {formData.rating === 3 && 'Good'}
                  {formData.rating === 4 && 'Very Good'}
                  {formData.rating === 5 && 'Excellent'}
                </span>
              </div>

              <div className={styles.ratingItem}>
                <label>Faculty / Teaching Quality</label>
                {renderStars('facultyRating', formData.facultyRating, hoverFaculty, setHoverFaculty)}
              </div>

              <div className={styles.ratingItem}>
                <label>Content Quality</label>
                {renderStars('contentQuality', formData.contentQuality, hoverContent, setHoverContent)}
              </div>

              <div className={styles.ratingItem}>
                <label>Doubt Resolution</label>
                {renderStars('doubtResolution', formData.doubtResolution, hoverDoubt, setHoverDoubt)}
              </div>

              <div className={styles.ratingItem}>
                <label>Test Series & Evaluation</label>
                {renderStars('testSeries', formData.testSeries, hoverTest, setHoverTest)}
              </div>

              <div className={styles.ratingItem}>
                <label>Platform / App Experience</label>
                {renderStars('platformExperience', formData.platformExperience, hoverPlatform, setHoverPlatform)}
              </div>
            </div>
          </div>

          {/* Section 4: Detailed Feedback */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIcon}>💬</span>
              <h2>Your Feedback</h2>
              <p className={styles.sectionSubtitle}>Share your thoughts, suggestions, or concerns</p>
            </div>

            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="feedbackType">
                  Feedback Type <span className={styles.required}>*</span>
                </label>
                <select
                  id="feedbackType"
                  name="feedbackType"
                  value={formData.feedbackType}
                  onChange={handleChange}
                  required
                  className={styles.formSelect}
                >
                  {feedbackTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="subject">
                  Subject <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Brief summary of your feedback"
                  required
                  className={styles.formInput}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="feedbackMessage">
                Feedback Details <span className={styles.required}>*</span>
              </label>
              <textarea
                id="feedbackMessage"
                name="feedbackMessage"
                value={formData.feedbackMessage}
                onChange={handleChange}
                placeholder="Please describe your experience in detail. What did you like? What could be improved?"
                rows="6"
                required
                className={styles.formTextarea}
              ></textarea>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="suggestions">
                Suggestions for Improvement
              </label>
              <textarea
                id="suggestions"
                name="suggestions"
                value={formData.suggestions}
                onChange={handleChange}
                placeholder="Do you have any ideas or suggestions that would help us serve you better?"
                rows="4"
                className={styles.formTextarea}
              ></textarea>
            </div>

            <div className={styles.formGroup}>
              <label>Would you recommend Exademy to others? <span className={styles.required}>*</span></label>
              <div className={styles.radioGroup}>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="recommend"
                    value="yes"
                    checked={formData.recommend === 'yes'}
                    onChange={handleChange}
                    required
                    className={styles.radio}
                  />
                  <span>Yes, definitely</span>
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="recommend"
                    value="maybe"
                    checked={formData.recommend === 'maybe'}
                    onChange={handleChange}
                    className={styles.radio}
                  />
                  <span>Maybe, need more time</span>
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="recommend"
                    value="no"
                    checked={formData.recommend === 'no'}
                    onChange={handleChange}
                    className={styles.radio}
                  />
                  <span>Not at this time</span>
                </label>
              </div>
            </div>

            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="allowContact"
                  checked={formData.allowContact}
                  onChange={handleChange}
                  className={styles.checkbox}
                />
                <span className={styles.checkboxText}>
                  I'm open to being contacted for follow-up questions regarding my feedback
                </span>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className={styles.formActions}>
            <button type="button" className={styles.resetBtn} onClick={() => window.scrollTo(0, 0)}>
              Reset Form
            </button>
            <button type="submit" className={styles.submitBtn}>
              Submit Feedback
            </button>
          </div>
        </form>

        {/* Feedback Guidelines */}
        <div className={styles.guidelinesSection}>
          <div className={styles.guidelinesCard}>
            <h3>📋 Feedback Guidelines</h3>
            <ul className={styles.guidelinesList}>
              <li>✓ Be specific and constructive in your feedback</li>
              <li>✓ Include both positive aspects and areas for improvement</li>
              <li>✓ Avoid personal attacks or inappropriate language</li>
              <li>✓ Your feedback helps us improve for thousands of students</li>
              <li>✓ Anonymous submissions are welcome and protected</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WriteFeedback;